import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const shuffle = (arr) => {  // ordre aléatoire à chaque visite : toutes les affiches/photos tournent
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const DURATION = 2000;
const PAUSE_AFTER_MANUAL = 8000; // après un clic sur une flèche, le défilement automatique fait une pause

// Cellule du ruban : cadre fixe (défilement stable) + indicateur doré pendant le chargement
const ScrollCell = ({ im, srcUrl, heightClass, testid }) => {
  const [ok, setOk] = useState(false);
  return (
    <div className={`${heightClass} aspect-[4/3] mx-2 flex items-center justify-center shrink-0 relative`}>
      {!ok && <Loader2 className="absolute w-5 h-5 text-gold/40 animate-spin" />}
      <img src={srcUrl} alt={im.name} data-testid={testid} onLoad={() => setOk(true)} loading="lazy"
        className={`max-h-full max-w-full object-contain rounded-lg transition-opacity duration-500 ${ok ? "opacity-100" : "opacity-0"}`} />
    </div>
  );
};

export function Diaporama({
  endpoint = "/public/diaporama",
  kicker = "/ Les spectacles",
  heading = null,
  testid = "diaporama",
  heightClass = "h-[50vh] md:h-[65vh]",
  widthClass = "max-w-[1400px]",
  caption = null,
  mode = "fade", // "fade" = une image à la fois | "scroll" = défilement continu de droite à gauche
}) {
  const [images, setImages] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  const idxRef = useRef(0);
  const pauseUntil = useRef(0);

  useEffect(() => {
    fetch(`${API}${endpoint}`)
      .then((r) => r.json())
      .then((d) => setImages(shuffle(d.images || [])))
      .catch(() => {});
  }, [endpoint]);

  useEffect(() => { idxRef.current = idx; }, [idx]);

  const src = (im) => `${API}${endpoint}/file/${im.id}`;
  const preload = (im) =>
    new Promise((res) => {
      const i = new window.Image();
      i.onload = i.onerror = () => res();
      i.src = src(im);
    });

  // Défilement : on précharge l'image suivante AVANT de changer de slide (pas de trou noir)
  useEffect(() => {
    if (images.length < 2) return;
    let cancelled = false;
    const tick = async () => {
      await preload(images[(idxRef.current + 1) % images.length]);
      if (!cancelled) setIdx((i) => (i + 1) % images.length);
    };
    const delay = Math.max(DURATION, pauseUntil.current - Date.now());
    timer.current = setTimeout(tick, delay);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [images, idx]);

  const go = useCallback(
    async (target) => {
      clearTimeout(timer.current);
      pauseUntil.current = Date.now() + PAUSE_AFTER_MANUAL;
      await preload(images[target]);
      setIdx(target);
    },
    [images]
  );
  const goPrev = () => images.length && go((idxRef.current - 1 + images.length) % images.length);
  const goNext = () => images.length && go((idxRef.current + 1) % images.length);

  if (!images.length) return null;

  const cur = images[idx];

  // Mode « scroll » : les visuels défilent attachés de droite à gauche en continu
  if (mode === "scroll") {
    return (
      <section data-testid={`${testid}-section`} className="py-20 md:py-28">
        <p className={`font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold text-center ${heading ? "mb-6" : "mb-12"}`}>
          {kicker}
        </p>
        {heading && (
          <h2 className="font-serif text-2xl md:text-3xl leading-tight text-center max-w-3xl mx-auto px-6 mb-10">
            {heading}
          </h2>
        )}
        <div className={`${widthClass} mx-auto px-6 md:px-10`}>
          <div className={`rounded-2xl overflow-hidden border border-white/10 bg-[#04162E] py-3`} data-testid={`${testid}-scroll`}>
            <Marquee speed={40} pauseOnHover gradient={false}>
              {images.map((im) => (
                <ScrollCell key={im.id} im={im} srcUrl={src(im)} heightClass={heightClass} testid={`${testid}-scroll-img`} />
              ))}
            </Marquee>
          </div>
          {caption && (
            <p data-testid={`${testid}-caption`} className="text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#A0B2CE]/70 mt-4">
              {caption}
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section data-testid={`${testid}-section`} className="py-20 md:py-28">
      <p className={`font-mono text-sm md:text-base tracking-[0.3em] uppercase text-gold text-center ${heading ? "mb-6" : "mb-12"}`}>
        {kicker}
      </p>
      {heading && (
        <h2 className="font-serif text-2xl md:text-3xl leading-tight text-center max-w-3xl mx-auto px-6 mb-10">
          {heading}
        </h2>
      )}
      <div className={`${widthClass} mx-auto px-6 md:px-10`}>
        <div className={`group relative ${heightClass} rounded-2xl overflow-hidden border border-white/10 bg-[#04162E]`}>
          {!loaded && <Loader2 data-testid={`${testid}-loading`} className="absolute inset-0 m-auto w-8 h-8 text-gold/60 animate-spin" />}
          <AnimatePresence initial={false}>
            <motion.div
              key={cur.id}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeInOut" }}
            >
              <img src={src(cur)} alt="" aria-hidden loading="lazy" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40" />
              <img src={src(cur)} alt={cur.name} onLoad={() => setLoaded(true)} data-testid={`${testid}-image`} loading="lazy"
                className="relative w-full h-full object-contain transition-transform duration-700 ease-out hover:scale-[1.04]" />
            </motion.div>
          </AnimatePresence>

          {images.length > 1 && (
            <>
              <button onClick={goPrev} data-testid={`${testid}-prev`} title="Image précédente"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-ink/60 backdrop-blur border border-white/15 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={goNext} data-testid={`${testid}-next`} title="Image suivante"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-ink/60 backdrop-blur border border-white/15 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div data-testid={`${testid}-counter`}
                className="absolute bottom-4 right-5 z-10 font-mono text-[11px] tracking-[0.15em] text-white/60 bg-ink/60 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
                {idx + 1} / {images.length}
              </div>
            </>
          )}
        </div>
        {caption && (
          <p data-testid={`${testid}-caption`} className="text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#A0B2CE]/70 mt-4">
            {caption}
          </p>
        )}
      </div>
    </section>
  );
}
